import { useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { DesignationType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import LoadingIndicator from 'components/LoadingIndicator';

import CreateDesignationDialogButton from './CreateDesignationDialogButton';
import UpdateDesignationDialog from './UpdateDesignationDialog';

const DESIGNATIONS_QUERY = gql(`
  query Designations($departmentId: ID!, $limit: Int, $cursor: ID) {
    department(id: $departmentId) {
      id
      designations(limit: $limit, cursor: $cursor) {
        nodes {
          id
          name
          employeeCount
          createdAt
        }
        pageInfo {
          hasNextPage
          cursor
          totalCount
        }
      }
    }
  }
`);

const DesignationsPage: FC = () => {
  const { departmentId } = useParams<{ departmentId: string }>();
  const [designationOnDisplay, setDesignationOnDisplay] = useState<DesignationType | null>(null);

  const { loading, error, data, refetch, fetchMore } = useQuery(DESIGNATIONS_QUERY, {
    variables: {
      departmentId: departmentId as string,
      limit: 50,
    },
    nextFetchPolicy: 'network-only', // This ensures empolyee count is up to date
    notifyOnNetworkStatusChange: true,
  });

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const designations = data.department.designations.nodes;
  const pageInfo = data.department.designations.pageInfo;

  return (
    <>
      <CreateDesignationDialogButton />
      {designationOnDisplay ? (
        <UpdateDesignationDialog
          id={designationOnDisplay.id}
          name={designationOnDisplay.name}
          onClose={() => setDesignationOnDisplay(null)}
        />
      ) : null}
      <DataTable
        data={designations as DesignationType[]}
        onClick={designation => setDesignationOnDisplay(designation)}
        columns={[
          {
            label: 'Designation Name',
            fieldName: 'name',
          },
          {
            label: 'Employee Count',
            fieldName: 'employeeCount',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
          },
        ]}
        paginationLoading={loading}
        hasNextPage={pageInfo.hasNextPage as boolean}
        onLoadMore={() => {
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          });
        }}
        totalCount={pageInfo.totalCount as number}
      />
    </>
  );
};

export default DesignationsPage;
